import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  CircularProgress, IconButton, InputAdornment, Divider
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Visibility, VisibilityOff,
  PlayArrow as DemoIcon, FamilyRestroom as FamilyIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeContext';
import api from '../../services/api';

const PINK = {
  main: '#e91e8c',
  light: '#f06292',
  dark: '#c2185b',
  hot: '#ff1493',
  bg: '#fce4ec',
  darkBg: '#2d1a24',
  gradient: 'linear-gradient(135deg, #e91e8c 0%, #f06292 50%, #ff1493 100%)',
  glow: '0 0 24px #e91e8c44, 0 8px 32px #e91e8c22',
  border: '#e91e8c33',
};

const cardMotion = {
  initial: { opacity: 0, y: 30, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const FamilyLoginPage = ({ onSuccess, onSetupRequired }) => {
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [, setPasswordRequired] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [showSetupChoice, setShowSetupChoice] = useState(false);
  const [inviteCodeRequired, setInviteCodeRequired] = useState(false);
  const [showInviteInput, setShowInviteInput] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [demoHints, setDemoHints] = useState(null);
  const [demoUrl, setDemoUrl] = useState(null);
  const [hasFamilyPassword, setHasFamilyPassword] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const { isDark } = useThemeMode();

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: isDark ? 'background.paper' : '#fff',
      '& fieldset': { borderColor: isDark ? '#444' : '#ddd' },
      '&:hover fieldset': { borderColor: PINK.light },
      '&.Mui-focused fieldset': { borderColor: PINK.main },
    },
    '& .MuiInputLabel-root': { color: isDark ? '#aaa' : '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: PINK.main },
    '& .MuiInputBase-input': {
      color: isDark ? '#fff' : '#1a1a1a',
      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 100px ${isDark ? '#2a2a3a' : '#fff'} inset`,
        WebkitTextFillColor: isDark ? '#fff' : '#1a1a1a',
        caretColor: isDark ? '#fff' : '#1a1a1a',
      },
    },
    '& .MuiFormHelperText-root': { color: isDark ? '#999' : '#888' },
  };

  useEffect(() => {
    const checkStatus = async () => {
      let demoMode = false, url = null;
      try {
        const setupResponse = await api.getSetupStatus();
        ({ isDemoMode: demoMode } = setupResponse.data);
        const { inviteCodeRequired: inviteReq, demoHints: hints, demoUrl: demoUrlVal, hasFamilyPassword: hasPw } = setupResponse.data;
        url = demoUrlVal || null;
        setIsDemoMode(demoMode);
        setInviteCodeRequired(inviteReq);
        setDemoHints(hints);
        setDemoUrl(url);
        setHasFamilyPassword(!!hasPw);
        if (hints?.familyPassword) setPassword(hints.familyPassword);

        const response = await api.verifyDevice();
        if (response.data?.noPasswordConfigured) {
          onSuccess?.();
          return;
        }
        setPasswordRequired(true);

        if (url && demoMode) {
          setShowSetupChoice(true);
        }
      } catch (err) {
        if (err.response?.data?.code === 'DEVICE_AUTH_REQUIRED') {
          setPasswordRequired(true);
          if (url && demoMode) {
            setShowSetupChoice(true);
          }
        } else {
          onSuccess?.();
          return;
        }
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, [onSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const deviceName = navigator.userAgent.substring(0, 100);
      const response = await api.familyLogin(password, deviceName);

      localStorage.setItem('family_device_token', response.data.deviceToken);
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Kirjautuminen epäonnistui');
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setError('');
    setLoading(true);

    try {
      const deviceName = `Demo-laite (${navigator.userAgent.substring(0, 50)})`;
      const response = await api.familyLogin('demo', deviceName);

      localStorage.setItem('family_device_token', response.data.deviceToken);
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Demo-kirjautuminen epäonnistui');
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    if (inviteCodeRequired && !showInviteInput) {
      setShowInviteInput(true);
      return;
    }

    if (inviteCodeRequired && !inviteCode.trim()) {
      setError('Kutsukoodi vaaditaan');
      return;
    }

    setError('');
    setResetting(true);

    try {
      await api.resetForSetup(inviteCode.trim());

      localStorage.removeItem('family_device_token');
      localStorage.removeItem('auth_token');

      onSetupRequired?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Nollaus epäonnistui');
      setResetting(false);
    }
  };

  const handleRecover = async () => {
    if (!recoveryCode.trim()) {
      setError('Kutsukoodi vaaditaan');
      return;
    }

    setError('');
    setRecovering(true);

    try {
      const res = await api.post('/auth/recover-with-invite', { inviteCode: recoveryCode.trim() });
      const { token, deviceToken } = res.data;

      localStorage.setItem('auth_token', token);
      localStorage.setItem('family_device_token', deviceToken);
      api.setAuthToken(token);

      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Palautus epäonnistui');
      setRecovering(false);
    }
  };

  if (loading && !error && !showSetupChoice) {
    return null;
  }

  // Demo mode choice UI
  if (showSetupChoice && (isDemoMode || demoUrl)) {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: isDark ? PINK.darkBg : PINK.bg, p: 2, position: 'relative', overflow: 'hidden',
      }}>
        {/* Ambient orbs */}
        <Box sx={{
          position: 'absolute', top: '-20%', right: '-10%',
          width: 400, height: 400, borderRadius: '50%',
          background: `radial-gradient(circle, ${PINK.main}15 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '-15%', left: '-10%',
          width: 350, height: 350, borderRadius: '50%',
          background: `radial-gradient(circle, ${PINK.light}12 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />

        <motion.div {...cardMotion} style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
          <Card sx={{
            borderRadius: 2,
            boxShadow: isDark
              ? `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${PINK.border}`
              : `0 8px 32px ${PINK.main}12, 0 0 0 1px ${PINK.border}`,
            bgcolor: isDark ? 'background.default' : '#fff',
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Box sx={{
                    width: 80, height: 80, borderRadius: '50%',
                    background: PINK.gradient,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: 'auto', mb: 2, boxShadow: PINK.glow, overflow: 'hidden',
                  }}>
                    <Box component="img" src="/icons/icon-96x96.png" alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: logoError ? 'none' : 'block' }}
                      onError={() => setLogoError(true)}
                    />
                    {logoError && <HomeIcon sx={{ width: 40, height: 40, color: 'white' }} />}
                  </Box>
                </motion.div>
                <Typography variant="h5" fontWeight={900} sx={{ color: isDark ? PINK.light : PINK.dark }}>
                  Perheen kotiaskareet
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: isDark ? '#aaa' : '#666' }}>
                  Tervetuloa! Kokeile demoa tai aloita oma perheesi.
                </Typography>
              </Box>

              {error && (
                <Typography color="error" variant="body2" role="alert" sx={{ mb: 2, textAlign: 'center' }}>
                  {error}
                </Typography>
              )}

              <AnimatePresence mode="wait">
                {showInviteInput ? (
                  <motion.div
                    key="invite"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <TextField
                      fullWidth
                      label="Kutsukoodi"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value)}
                      disabled={resetting}
                      autoFocus
                      sx={{ mb: 2, ...inputSx }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleStartSetup}
                      disabled={resetting || !inviteCode.trim()}
                      startIcon={resetting ? <CircularProgress size={20} color="inherit" /> : <FamilyIcon />}
                      sx={{
                        py: 1.5, fontWeight: 700, borderRadius: 2,
                        background: PINK.gradient, color: '#fff',
                        boxShadow: `0 4px 16px ${PINK.main}44`,
                        '&:hover': { boxShadow: `0 6px 20px ${PINK.main}66` },
                        mb: 1,
                      }}
                    >
                      {resetting ? 'Valmistellaan...' : 'Jatka'}
                    </Button>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => { setShowInviteInput(false); setError(''); }}
                      disabled={resetting}
                      sx={{ color: PINK.main }}
                    >
                      Takaisin
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="choices"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        {...(demoUrl
                          ? { component: 'a', href: demoUrl, target: '_blank', rel: 'noopener noreferrer' }
                          : { onClick: handleDemoLogin, disabled: loading }
                        )}
                        startIcon={loading && !demoUrl ? <CircularProgress size={20} /> : <DemoIcon />}
                        sx={{
                          py: 2, borderColor: PINK.main, color: PINK.main, borderRadius: 2,
                          '&:hover': { borderColor: PINK.hot, bgcolor: `${PINK.main}10` },
                          fontWeight: 600,
                        }}
                      >
                        Kokeile demoa
                      </Button>

                      <Divider sx={{ my: 1 }}>
                        <Typography variant="caption" sx={{ color: isDark ? '#999' : '#888' }}>tai</Typography>
                      </Divider>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleStartSetup}
                        disabled={resetting}
                        startIcon={resetting ? <CircularProgress size={20} color="inherit" /> : <FamilyIcon />}
                        sx={{
                          py: 2, fontWeight: 700, borderRadius: 2,
                          background: PINK.gradient, color: '#fff',
                          boxShadow: `0 4px 16px ${PINK.main}44`,
                          '&:hover': { boxShadow: `0 6px 20px ${PINK.main}66` },
                        }}
                      >
                        Aloita oma perhe
                      </Button>

                      {hasFamilyPassword && (
                        <>
                          <Divider sx={{ my: 1 }}>
                            <Typography variant="caption" sx={{ color: isDark ? '#999' : '#888' }}>tai</Typography>
                          </Divider>
                          <Button
                            fullWidth
                            variant="text"
                            onClick={() => setShowSetupChoice(false)}
                            sx={{ color: PINK.main, fontWeight: 600 }}
                          >
                            Kirjaudu olemassa olevaan perheeseen
                          </Button>
                        </>
                      )}

                      <Typography variant="caption" sx={{ textAlign: 'center', mt: 1, color: isDark ? '#999' : '#888' }}>
                        Demo-tilassa voit tutustua sovellukseen vapaasti.
                        <br />
                        Oman perheen aloitus poistaa demo-tilit.
                      </Typography>
                      {demoHints && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: isDark ? 'background.container' : `${PINK.main}08`, borderRadius: 2 }}>
                          <Typography variant="caption" component="div" sx={{ textAlign: 'center', color: isDark ? '#999' : '#888' }}>
                            <strong>Demo-tunnukset:</strong><br />
                            Vanhempi: {demoHints.parentCredentials}<br />
                            Kioski: {demoHints.kioskCredentials}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>
      </Box>
    );
  }

  // Normal family password login
  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: isDark ? PINK.darkBg : PINK.bg, p: 2, position: 'relative', overflow: 'hidden',
    }}>
      {/* Ambient orbs */}
      <Box sx={{
        position: 'absolute', top: '-20%', right: '-10%',
        width: 400, height: 400, borderRadius: '50%',
        background: `radial-gradient(circle, ${PINK.main}15 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '-15%', left: '-10%',
        width: 350, height: 350, borderRadius: '50%',
        background: `radial-gradient(circle, ${PINK.light}12 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <motion.div {...cardMotion} style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        <Card sx={{
          borderRadius: 2,
          boxShadow: isDark
            ? `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${PINK.border}`
            : `0 8px 32px ${PINK.main}12, 0 0 0 1px ${PINK.border}`,
          bgcolor: isDark ? 'background.default' : '#fff',
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Box sx={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: PINK.gradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mx: 'auto', mb: 2, boxShadow: PINK.glow, overflow: 'hidden',
                }}>
                  <Box component="img" src="/icons/icon-96x96.png" alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<svg style="width:40px;height:40px;color:white" viewBox="0 0 24 24"><path fill="currentColor" d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z"/></svg>'; }}
                  />
                </Box>
              </motion.div>
              <Typography variant="h5" fontWeight={900} sx={{ color: isDark ? PINK.light : PINK.dark }}>
                Perheen kotiaskareet
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: isDark ? '#aaa' : '#666' }}>
                Käyttäjät saavat kotiaskareista pisteitä, bonus kolikoita sekä viikkorahaa ja shoppailevat palkintoja tai elämyksiä kaupasta.
              </Typography>
            </Box>

            <motion.form
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <TextField
                fullWidth
                label="Perheen salasana"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!error}
                helperText={error || (demoHints ? `Demo-salasana: ${demoHints.familyPassword}` : '')}
                disabled={loading}
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" aria-label={showPassword ? 'Piilota salasana' : 'Näytä salasana'}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  mb: 3,
                  ...inputSx,
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !password}
                sx={{
                  py: 1.5, fontWeight: 700, borderRadius: 2,
                  background: PINK.gradient, color: '#fff',
                  boxShadow: `0 4px 16px ${PINK.main}44`,
                  '&:hover': { boxShadow: `0 6px 20px ${PINK.main}66` },
                  '&.Mui-disabled': { background: '#eee', color: '#aaa' },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Kirjaudu sisään'
                )}
              </Button>
            </motion.form>

            <Typography
              variant="caption"
              sx={{ display: 'block', textAlign: 'center', mt: 3, color: isDark ? '#777' : '#888' }}
            >
              Tämä laite muistetaan 90 päivää
            </Typography>

            {!showRecovery ? (
              <Typography
                variant="caption"
                onClick={() => { setShowRecovery(true); setError(''); }}
                sx={{ display: 'block', textAlign: 'center', mt: 1, color: PINK.main, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                Unohdin salasanan
              </Typography>
            ) : (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Kutsukoodi"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  disabled={recovering}
                  size="small"
                  autoFocus
                  sx={{ mb: 1.5, ...inputSx }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRecover(); }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleRecover}
                  disabled={recovering || !recoveryCode.trim()}
                  sx={{
                    py: 1, fontWeight: 600, borderRadius: 2, fontSize: '0.85rem',
                    background: PINK.gradient, color: '#fff',
                    '&.Mui-disabled': { background: '#eee', color: '#aaa' },
                  }}
                >
                  {recovering ? <CircularProgress size={20} color="inherit" /> : 'Kirjaudu kutsukoodilla'}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  onClick={() => { setShowRecovery(false); setRecoveryCode(''); setError(''); }}
                  sx={{ mt: 0.5, color: isDark ? '#777' : '#888' }}
                >
                  Peruuta
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default FamilyLoginPage;
